import type { FeedbackState } from '../../../types/feedback';
import {
    BookOpen,
    Brain,
    Headphones,
    Lightbulb,
    MessageCircle,
    MessageSquare,
    Play,
    Users,
    Zap,
    type LucideIcon,
} from 'lucide-react';

export interface LegendItem {
    icon: React.ReactNode;
    label: string;
    key: string;
    total: number;
}

export interface TimeSection {
    title: string;
    states: FeedbackState[];
}

interface ActionConfig {
    icon: LucideIcon;
    label: string;
}

// Centralized action type configuration
export const ACTION_CONFIG: Record<string, ActionConfig> = {
    obsessionFlowCompleted: {
        icon: Brain,
        label: 'Obsession Flow',
    },
    distressFlowCompleted: {
        icon: Zap,
        label: 'Distress Flow',
    },
    compulsionFlowCompleted: {
        icon: BookOpen,
        label: 'Compulsion Flow',
    },
    chatPageVisited: {
        icon: MessageCircle,
        label: 'Chat Page',
    },
    questChatPageVisited: {
        icon: MessageSquare,
        label: 'Quest Chat',
    },
    listenedToAudioLesson: {
        icon: Headphones,
        label: 'Audio Lesson',
    },
    lessonCompleted: {
        icon: BookOpen,
        label: 'Lesson Completed',
    },
    readQuestInsight: {
        icon: Lightbulb,
        label: 'Quest Insight',
    },
    answeredCommunityQuestion: {
        icon: Users,
        label: 'Community Answer',
    },
    watchedCommunityVideo: {
        icon: Play,
        label: 'Community Video',
    },
};

// Helper function to get action icon
export const getActionIcon = (actionType: string, iconProps: { size: number; className: string }) => {
    // Normalize the action type to match our keys (convert from camelCase or lowercase)
    const normalizedAction = actionType
        .toLowerCase()
        .replace(/[^a-z]/g, '');

    // Find matching config key
    const configKey = Object.keys(ACTION_CONFIG).find(
        key => key.toLowerCase() === normalizedAction
    );

    if (configKey) {
        const IconComponent = ACTION_CONFIG[configKey].icon;
        return <IconComponent {...iconProps} />;
    }

    // Default fallback
    return <BookOpen {...iconProps} />;
};

export const getLegendItems = (feedbackStates: FeedbackState[]): LegendItem[] => {
    // Calculate totals for each action type across all feedback states
    const actionTotals: Record<string, number> = {};

    feedbackStates.forEach((state) => {
        const actionEntries = Object.entries(state.actionCounts || {}) as [string, number][];
        actionEntries.forEach(([action, count]) => {
            actionTotals[action] = (actionTotals[action] || 0) + count;
        });
    });

    const iconProps = { size: 16, className: 'text-gray-600' };

    // Build items from ACTION_CONFIG
    const items = Object.entries(ACTION_CONFIG).map(([key, config]) => {
        const IconComponent = config.icon;
        return {
            key,
            icon: <IconComponent {...iconProps} />,
            label: config.label,
            total: actionTotals[key] || 0,
        };
    });

    // Sort alphabetically by key to match the card display order
    return items.sort((a, b) => a.key.localeCompare(b.key));
};

export const getTimeSections = (feedbackStates: FeedbackState[]): TimeSection[] => {
    const now = Date.now();
    const oneHour = 60 * 60 * 1000;
    const sixHours = 6 * oneHour;
    const twelveHours = 12 * oneHour;
    const twentyFourHours = 24 * oneHour;
    const oneWeek = 7 * twentyFourHours;
    const oneMonth = 30 * twentyFourHours;

    const sections: TimeSection[] = [
        { title: 'Last Hour', states: [] },
        { title: 'Last 6 Hours', states: [] },
        { title: 'Last 12 Hours', states: [] },
        { title: 'Last 24 Hours', states: [] },
        { title: 'Last Week', states: [] },
        { title: 'Last Month', states: [] },
        { title: 'Older', states: [] },
    ];

    feedbackStates.forEach((state) => {
        const updatedAt = state.updatedAt ? (typeof state.updatedAt === 'number' ? state.updatedAt : state.updatedAt.toMillis()) : 0;

        const timeDiff = now - updatedAt;

        if (timeDiff <= oneHour) {
            sections[0].states.push(state);
        } else if (timeDiff <= sixHours) {
            sections[1].states.push(state);
        } else if (timeDiff <= twelveHours) {
            sections[2].states.push(state);
        } else if (timeDiff <= twentyFourHours) {
            sections[3].states.push(state);
        } else if (timeDiff <= oneWeek) {
            sections[4].states.push(state);
        } else if (timeDiff <= oneMonth) {
            sections[5].states.push(state);
        } else {
            sections[6].states.push(state);
        }
    });

    // Filter out empty sections
    return sections.filter((section) => section.states.length > 0);
};

